/** My account — self-service profile edit + password change. Reachable from
 *  the header avatar; available to every signed-in role, super-admins included
 *  (who previously had no account surface at all). */
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { myAiUsage, type MyAiUsage } from "../../api/aiUsage";
import * as authApi from "../../api/auth";
import { ApiError } from "../../api/http";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/ToastContext";
import { Button, Card, Input, Label, Select } from "../../components/ui";
import { UsageBar } from "../../components/UsageBar";
import { useT } from "../../i18n";
import { TwoFactorSection } from "./TwoFactorSection";

export function ProfilePage() {
  const t = useT();
  const { user, refresh } = useAuth();
  const { push } = useToast();
  const isTenantUser = Boolean(user?.tenant_id);
  const [usage, setUsage] = useState<MyAiUsage | null>(null);

  useEffect(() => {
    if (!isTenantUser) return;
    myAiUsage()
      .then(setUsage)
      .catch(() => {
        // AI features may be off for this tenant — just hide the card.
      });
  }, [isTenantUser]);

  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [language, setLanguage] = useState(user?.language_preference ?? "de");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [savingPw, setSavingPw] = useState(false);

  const onSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await authApi.updateProfile({
        full_name: fullName,
        phone,
        language_preference: language,
      });
      await refresh();
      push("success", t("profile.saved"));
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setSavingProfile(false);
    }
  };

  const onChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (newPw !== confirmPw) {
      setPwError(t("profile.password.mismatch"));
      return;
    }
    setSavingPw(true);
    try {
      await authApi.changePassword(currentPw, newPw);
      push("success", t("profile.password.changed"));
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      setPwError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{t("profile.title")}</h1>
        <p className="mt-1 text-muted">{t("profile.subtitle")}</p>
      </div>

      <Card title={t("profile.details")}>
        <form onSubmit={onSaveProfile} className="space-y-4">
          <div>
            <Label htmlFor="full_name">{t("profile.fullName")}</Label>
            <Input
              id="full_name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" type="email" value={user?.email ?? ""} disabled readOnly />
            <p className="mt-1 text-xs text-muted">{t("profile.emailLocked")}</p>
          </div>
          <div>
            <Label htmlFor="phone">{t("profile.phone")}</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="language">{t("profile.language")}</Label>
            <Select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </Select>
          </div>
          <Button type="submit" loading={savingProfile}>
            {t("common.save")}
          </Button>
        </form>
      </Card>

      <Card title={t("profile.password.title")}>
        <form onSubmit={onChangePassword} className="space-y-4">
          <div>
            <Label htmlFor="current_pw">{t("profile.password.current")}</Label>
            <Input
              id="current_pw"
              type="password"
              autoComplete="current-password"
              required
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="new_pw">{t("profile.password.new")}</Label>
            <Input
              id="new_pw"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="confirm_pw">{t("profile.password.confirm")}</Label>
            <Input
              id="confirm_pw"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
            />
          </div>
          {pwError && (
            <p role="alert" className="text-sm text-danger">
              {pwError}
            </p>
          )}
          <Button type="submit" loading={savingPw}>
            {t("profile.password.submit")}
          </Button>
        </form>
      </Card>

      <TwoFactorSection />

      {isTenantUser && usage && (
        <Card title={t("profile.aiUsage")}>
          <div className="space-y-4">
            <UsageBar
              label={t("usage.thisWeek")}
              used={usage.week.used}
              limit={usage.week.limit}
            />
            <UsageBar
              label={t("usage.thisMonth")}
              used={usage.month.used}
              limit={usage.month.limit}
            />
          </div>
        </Card>
      )}
    </div>
  );
}
